// Failure simulation (SM-753): the current epoch's fault rules — kind,
// scope, match, one-shot, hits, expiry countdown and the server's own
// statement of whether provider state commits — plus the arm form for
// all 14 kinds with each kind's extra fields, and disable.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Check, Field, Select, TextInput } from "@/components/ui/Field";
import { Empty, Mono, PageHeader, ReadOnlyHint, TableWrap } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { api } from "@/lib/api";
import { ENDPOINTS } from "@/lib/constants";
import { buildFaultBody, extraFieldsFor, type FaultForm, type Scope } from "@/lib/faults";
import { endpointName, fmtCountdown, fmtTime } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";
import { useCanMutate } from "@/lib/session";
import type { FaultRule } from "@/lib/types";
import { useNow } from "@/lib/useNow";

export const Route = createFileRoute("/_app/faults")({
  component: FaultsPage,
});

function ruleState(r: FaultRule, now: number): { label: string; tone: "accent" | "neutral" | "no" | "warn" } {
  if (r.disabled_at) return { label: "DISABLED", tone: "no" };
  if (r.consumed_at) return { label: "CONSUMED", tone: "neutral" };
  if (new Date(r.expires_at).getTime() <= now) return { label: "EXPIRED", tone: "neutral" };
  return { label: "ARMED", tone: "accent" };
}

function FaultsPage() {
  const [activeOnly, setActiveOnly] = useState(true);
  const faults = useQuery({ queryKey: keys.faults(activeOnly), queryFn: () => fetchers.faults(activeOnly), refetchInterval: 5_000 });
  const env = useQuery({ queryKey: keys.environment(), queryFn: fetchers.environment });
  return (
    <div className="space-y-5">
      <PageHeader
        title="Failure simulation"
        subtitle={
          <>
            Rules apply to the current scenario epoch only{env.data ? ` (epoch ${env.data.epoch})` : ""} and always expire. A reset never leaks
            one into the next epoch.
          </>
        }
      />
      <SectionCard
        title="Fault rules"
        actions={
          <div className="seg" role="radiogroup" aria-label="Which rules">
            <button type="button" className={activeOnly ? "is-active" : undefined} onClick={() => setActiveOnly(true)}>
              Active
            </button>
            <button type="button" className={!activeOnly ? "is-active" : undefined} onClick={() => setActiveOnly(false)}>
              All this epoch
            </button>
          </div>
        }
      >
        <ErrorBanner error={faults.error} />
        {faults.isLoading ? <Skeleton lines={4} /> : <RulesTable rows={faults.data ?? []} />}
      </SectionCard>
      <ArmForm kinds={env.data?.fault_kinds ?? []} />
    </div>
  );
}

function RulesTable({ rows }: { rows: FaultRule[] }) {
  const now = useNow(1000);
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const [target, setTarget] = useState<FaultRule | null>(null);
  const [reason, setReason] = useState("");
  if (rows.length === 0) return <Empty>No fault rules. Every provider call answers normally.</Empty>;
  return (
    <>
      {!canMutate && <ReadOnlyHint />}
      <TableWrap>
        <table className="tbl" data-testid="fault-rules">
          <thead>
            <tr>
              <th>State</th>
              <th>Kind</th>
              <th>Scope / match</th>
              <th>One-shot</th>
              <th>Hits</th>
              <th>Expires</th>
              <th>Commit meaning</th>
              <th>Extras</th>
              <th>Note</th>
              <th>Rule</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const st = ruleState(r, now);
              const live = st.label === "ARMED";
              return (
                <tr key={r.rule_id}>
                  <td>
                    <Badge tone={st.tone}>{st.label}</Badge>
                  </td>
                  <td>
                    <Mono className="font-semibold">{r.kind}</Mono>
                  </td>
                  <td>
                    <div className="text-xs">
                      <b>{r.scope}</b>
                      {r.endpoint && <span className="mono"> · {endpointName(r.endpoint)}</span>}
                      {r.match_user_id && <span className="mono"> · user {r.match_user_id}</span>}
                      {r.match_transaction_id && <span className="mono"> · txn {r.match_transaction_id}</span>}
                    </div>
                  </td>
                  <td>{r.one_shot ? "yes" : "no (persistent)"}</td>
                  <td>{r.hits}</td>
                  <td>
                    <span className={live ? "font-semibold text-t-1" : "t-mute"}>{fmtCountdown(r.expires_at, now)}</span>
                    <div className="text-2xs t-mute">{fmtTime(r.expires_at)}</div>
                  </td>
                  <td className="wrap text-xs">{r.commit_meaning}</td>
                  <td className="text-xs">
                    <Extras r={r} />
                  </td>
                  <td className="wrap text-xs">{r.note || <span className="t-mute">—</span>}</td>
                  <td>
                    <Mono className="text-2xs">{r.rule_id}</Mono>
                    <div className="text-2xs t-mute">by {r.created_by}</div>
                  </td>
                  <td>
                    {live && (
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={!canMutate}
                        onClick={() => {
                          setReason("");
                          setTarget(r);
                        }}
                      >
                        Disable
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableWrap>
      <ConfirmDialog
        open={!!target}
        title="Disable this fault rule?"
        tone="primary"
        confirmLabel="Disable rule"
        consequence={
          target && (
            <>
              <span className="mono">{target.kind}</span> ({target.scope}) stops applying immediately; the next matching
              provider call answers normally. The rule stays listed as DISABLED.
            </>
          )
        }
        onConfirm={async () => {
          if (!target) return;
          await api(`/faults/${encodeURIComponent(target.rule_id)}?reason=${encodeURIComponent(reason.trim())}`, { method: "DELETE" });
          await qc.invalidateQueries({ queryKey: ["mock", "faults"] });
        }}
        onClose={() => setTarget(null)}
      >
        <Field label="Reason (optional, audited)">
          <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </ConfirmDialog>
    </>
  );
}

function Extras({ r }: { r: FaultRule }) {
  const ex = extraFieldsFor(r.kind);
  const parts: string[] = [];
  if (ex.includes("latency")) parts.push(`${r.latency_ms} ms ± ${r.jitter_ms} ms`);
  if (ex.includes("retry_after")) parts.push(`Retry-After ${r.retry_after_s}s`);
  if (ex.includes("pending")) parts.push(`${r.pending_ms} ms → ${r.pending_outcome}`);
  if (ex.includes("commit_before_fault")) parts.push(r.commit_before_fault ? "commits first" : "no commit");
  return parts.length ? <span className="mono">{parts.join(" · ")}</span> : <span className="t-mute">—</span>;
}

const TTL_PRESETS: Array<[string, string]> = [
  ["300", "5 minutes"],
  ["3600", "1 hour (default)"],
  ["21600", "6 hours"],
  ["86400", "1 day"],
  ["604800", "7 days (max)"],
];

const EMPTY: FaultForm = {
  kind: "insufficient_balance",
  scope: "endpoint",
  endpoint: "/wallet/v1/Deduct",
  matchUserId: "",
  matchTransactionId: "",
  oneShot: true,
  commitBeforeFault: false,
  latencyMs: "2000",
  jitterMs: "0",
  retryAfterS: "1",
  pendingMs: "30000",
  pendingOutcome: "COMMIT",
  ttlSeconds: "3600",
  note: "",
};

function ArmForm({ kinds }: { kinds: Array<{ kind: string; commit_meaning: string }> }) {
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const [f, setF] = useState<FaultForm>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [armed, setArmed] = useState<FaultRule | null>(null);
  const built = buildFaultBody(f);
  const extras = extraFieldsFor(f.kind);
  const meaning = kinds.find((k) => k.kind === f.kind)?.commit_meaning;
  const upd = <K extends keyof FaultForm>(k: K, v: FaultForm[K]) => setF((s) => ({ ...s, [k]: v }));

  return (
    <SectionCard title="Arm a fault rule">
      {!canMutate && <ReadOnlyHint />}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!built.ok) return;
          setBusy(true);
          setError(null);
          try {
            const rule = await api<FaultRule>("/faults", { method: "POST", body: built.body });
            setArmed(rule);
            await qc.invalidateQueries({ queryKey: ["mock", "faults"] });
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset disabled={!canMutate || busy}>
          <div className="form-grid">
            <Field label={`Kind (${kinds.length})`}>
              <Select
                value={f.kind}
                onChange={(e) => {
                  const kind = e.target.value;
                  // a latency profile is persistent unless asked otherwise (server default)
                  setF((s) => ({ ...s, kind, oneShot: kind === "latency" ? false : s.kind === "latency" ? true : s.oneShot }));
                }}
                data-testid="fault-kind"
              >
                {kinds.map((k) => (
                  <option key={k.kind}>{k.kind}</option>
                ))}
              </Select>
            </Field>
            <Field label="Scope">
              <Select value={f.scope} onChange={(e) => upd("scope", e.target.value as Scope)}>
                <option value="endpoint">endpoint — every call to one endpoint</option>
                <option value="user">user — calls for one user</option>
                <option value="transaction">transaction — calls naming one transaction</option>
              </Select>
            </Field>
            <Field label={f.scope === "endpoint" ? "Endpoint" : "Endpoint (optional narrowing)"}>
              <Select value={f.endpoint} onChange={(e) => upd("endpoint", e.target.value)}>
                {f.scope !== "endpoint" && <option value="">Any endpoint</option>}
                {ENDPOINTS.map((ep) => (
                  <option key={ep} value={ep}>
                    {endpointName(ep)}
                  </option>
                ))}
              </Select>
            </Field>
            {f.scope === "user" && (
              <Field label="Match user id">
                <TextInput value={f.matchUserId} onChange={(e) => upd("matchUserId", e.target.value)} className="mono" />
              </Field>
            )}
            {f.scope === "transaction" && (
              <Field label="Match transaction id">
                <TextInput value={f.matchTransactionId} onChange={(e) => upd("matchTransactionId", e.target.value)} className="mono" />
              </Field>
            )}
            {extras.includes("latency") && (
              <>
                <Field label="Latency (ms)">
                  <TextInput value={f.latencyMs} onChange={(e) => upd("latencyMs", e.target.value)} inputMode="numeric" className="mono" />
                </Field>
                <Field label="Jitter (ms)" hint="Up to this much extra, random per call.">
                  <TextInput value={f.jitterMs} onChange={(e) => upd("jitterMs", e.target.value)} inputMode="numeric" className="mono" />
                </Field>
              </>
            )}
            {extras.includes("retry_after") && (
              <Field label="Retry-After (s)">
                <TextInput value={f.retryAfterS} onChange={(e) => upd("retryAfterS", e.target.value)} inputMode="numeric" className="mono" />
              </Field>
            )}
            {extras.includes("pending") && (
              <>
                <Field label="Pending for (ms)">
                  <TextInput value={f.pendingMs} onChange={(e) => upd("pendingMs", e.target.value)} inputMode="numeric" className="mono" />
                </Field>
                <Field label="Then">
                  <Select value={f.pendingOutcome} onChange={(e) => upd("pendingOutcome", e.target.value as "COMMIT" | "FAIL")}>
                    <option value="COMMIT">COMMIT</option>
                    <option value="FAIL">FAIL</option>
                  </Select>
                </Field>
              </>
            )}
            <Field label="Expires after (TTL)">
              <Select value={f.ttlSeconds} onChange={(e) => upd("ttlSeconds", e.target.value)}>
                {TTL_PRESETS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Note (audited)" className="span-all">
              <TextInput value={f.note} onChange={(e) => upd("note", e.target.value)} placeholder="test case this rule is for" />
            </Field>
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <Check
              label="One-shot — applies to the next matching request only"
              hint={f.kind === "latency" ? "A latency profile is persistent by default and combines with one other rule." : undefined}
              checked={f.oneShot}
              onChange={(e) => upd("oneShot", e.target.checked)}
            />
            {extras.includes("commit_before_fault") && (
              <Check
                label="Commit before the fault"
                hint="Provider state commits first, then the answer is replaced — the ambiguous-outcome case."
                checked={f.commitBeforeFault}
                onChange={(e) => upd("commitBeforeFault", e.target.checked)}
              />
            )}
          </div>
          {meaning && (
            <div className="banner mt-4" role="note">
              <span aria-hidden>ⓘ</span>
              <span>
                <b className="mono">{f.kind}</b>: {meaning}
              </span>
            </div>
          )}
          {!built.ok && <p className="mt-2 text-xs text-no">{built.error}</p>}
          <ErrorBanner error={error} className="mt-3" />
          {armed && (
            <div className="banner mt-3" role="status" style={{ borderColor: "var(--yes)" }}>
              <span aria-hidden>✓</span>
              <span>
                Armed <span className="mono">{armed.rule_id}</span> ({armed.kind}), expires {fmtTime(armed.expires_at)}.
              </span>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <Button type="submit" loading={busy} disabled={!built.ok}>
              Arm rule
            </Button>
          </div>
        </fieldset>
      </form>
    </SectionCard>
  );
}
